// Multi-op machining tasks (Lathe / Mill): ONE task row, N machine setups.
//
// The API puts op_count / current_op / current_op_label on the queue rows and
// (since jobq-api 2026-09-10) on GET /jobs/{id}/progress, which is the payload
// the workstation detail panel actually builds its task from. Every button
// decision on that panel goes through this helper so the In Progress and Paused
// branches cannot drift apart. An ordinary task (op_count null) reads as
// "nothing multi-op", which keeps every pre-existing card exactly as it was.
export function multiOpFlow(task) {
  const opCount = Number(task?.op_count) || 0;
  if (!opCount) {
    return { multiOp: false, opCount: null, currentOp: 1, hasMoreOps: false, label: null };
  }
  const currentOp = Number(task.current_op) || 1;
  return {
    multiOp: true,
    opCount,
    currentOp,
    // Another setup follows this one: the way forward is Finish Op, never
    // Complete (the API refuses Complete with 409 until the last op).
    hasMoreOps: currentOp < opCount,
    label: task.current_op_label || `Op ${currentOp}`,
  };
}

// "1:07:42.600000", "0:33:51", "1 day, 2:00:00" (psycopg2 interval strings the
// API returns) -> seconds. Anything unparseable reads as 0.
export function intervalToSeconds(value) {
  if (value == null) return 0;
  if (typeof value === 'number') return value;
  let rest = String(value).trim();
  let days = 0;
  const dm = rest.match(/^(-?\d+)\s+days?,\s*(.*)$/);
  if (dm) { days = Number(dm[1]); rest = dm[2]; }
  const parts = rest.split(':').map(Number);
  if (!parts.length || parts.some((n) => Number.isNaN(n))) return 0;
  const [h, m, s] = parts.length === 3 ? parts : parts.length === 2 ? [0, ...parts] : [0, 0, ...parts];
  return days * 86400 + h * 3600 + m * 60 + s;
}

export function fmtSeconds(secs) {
  const s = Math.max(0, Math.round(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h) return `${h}h ${m}m`;
  return m ? `${m}m ${r}s` : `${r}s`;
}

// What Auto-Complete would do on this task, or null when it must not be offered.
//
// An ordinary task is auto-completable only while Pending (the API refuses any
// other status). A multi-op task is auto-completable ONE OP AT A TIME, while
// the current op is untouched: Pending (op 1 never started) or Paused between
// ops (current_op > 1 — the previous op was just handed on by Finish Op). A
// task Paused on op 1 was started and paused, so op 1 has recorded time and
// the API would refuse; the button is not shown for it. The API books an equal
// share (1/op_count) of the task estimate per op — the same split is shown in
// the confirm so the operator knows what is being recorded.
export function autoCompletePlan(task) {
  const flow = multiOpFlow(task);
  const status = task?.status;
  const est = intervalToSeconds(task?.estimated_time);
  if (!flow.multiOp) {
    if (status !== 'Pending') return null;
    return {
      multiOp: false, label: 'Auto-Complete', opLabel: null, lastOp: true,
      opCount: 1, currentOp: 1, nextOp: null,
      bookedSeconds: est > 0 ? est : 15 * 60, estimateSeconds: est,
    };
  }
  const untouched = status === 'Pending' || (status === 'Paused' && flow.currentOp > 1);
  if (!untouched) return null;
  const lastOp = !flow.hasMoreOps;
  return {
    multiOp: true,
    label: lastOp ? `Auto-Complete ${flow.label} (last op)` : `Auto-Complete ${flow.label}`,
    opLabel: flow.label,
    lastOp,
    opCount: flow.opCount,
    currentOp: flow.currentOp,
    nextOp: lastOp ? null : flow.currentOp + 1,
    bookedSeconds: (est > 0 ? est : 15 * 60) / flow.opCount,
    estimateSeconds: est,
  };
}
